import {
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type WithColumns,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import {
  ELEMENT_PATH_WF,
  FORM_GROUP_ACTION_OPTIONS,
  type FormGroupActionType,
  type IPropEditorResolver,
  type PropertiesConfig,
  type PropertiesConfigItemDefinition,
} from '@blaze-shared/elements-domain';
import { DEFAULT_SCHEMA } from '@blaze-shared/schema-domain';

import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { FormGroupChildrenHandlerService } from './form-group-children-handler.service';
import { FormGroupOutputEmitGuard } from './form-group-output-emit.guard';
import { FormGroupComponent } from './form-group.component';
import { type FormGroupPropsModel } from './form-group.model';
import { FormGroupPropsBuilder } from './form-group.props-builder';
import { FormGroupPropsExtractor } from './form-group.props-extractor';

export type FormGroupDef = WithColumns & WithProps<FormGroupPropsModel> & WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    formGroup: FormGroupDef;
  }
}

export const FORM_GROUP_MAIN_SLOT_ID = '0';

const FORM_GROUP_WIDTH = 380;
const FORM_GROUP_HEIGHT = 420;

registerElementDefinition({
  type: 'formGroup',
  componentType: FormGroupComponent,
  outputEmitGuard: FormGroupOutputEmitGuard,
  childrenHandler: FormGroupChildrenHandlerService,
  isListed: true,
  name: 'Form Group',
  width: { mode: 'value', value: FORM_GROUP_WIDTH },
  // By default the element should have a min-height and grow automatically if needed
  height: { mode: 'value', value: FORM_GROUP_HEIGHT, fixed: false },
  slots: {
    ids: [FORM_GROUP_MAIN_SLOT_ID],
    [FORM_GROUP_MAIN_SLOT_ID]: {
      children: [],
    },
  },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  propsBuilder: FormGroupPropsBuilder,
  propsExtractor: FormGroupPropsExtractor,
  initial: getInitialValues(),
  defaults: getDefaults(),
  tags: ['form'],
  groups: ['forms'],
  styleIncludePaths: ['style'],
  features: {
    visualStyle: 'block',
  },
  version: 1,
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [{ eventType: 'change', schema: DEFAULT_SCHEMA }],
  };
}

function getActionTypeConfig(actionType: 'add' | 'edit'): PropertiesConfigItemDefinition<'radios'> {
  const options = [
    { value: 'Page', label: 'Open Page' },
    { value: 'None', label: 'None' },
  ];
  if (actionType === 'add') {
    options.unshift({ value: 'Clear', label: 'Clear Form' });
  }
  return epc.utils.conditionalField(
    epc.fields.radiosField('content.postAction.actionType', 'After action', options, {
      wrapped: true,
    }),
    'content.action',
    (model: FormGroupActionType) => model !== actionType,
  );
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('formGroup', [
    epc.section('content', 'Content', [
      epc.subsections.subsection(
        'data',
        'Source',
        [
          epc.fields.selectDataSourceField(
            { key: 'value', label: '', sources: 'list', bindings: [] },
            'form-group-action',
            '',
            { hideLabel: false, hasRefresh: true },
          ),
          epc.fields.textInfo('', 'New data will trigger this action in the table', {
            header: true,
          }),
          epc.fields.selectFieldFromList('content.action', '', FORM_GROUP_ACTION_OPTIONS, {
            hideLabel: true,
            showSelection: true,
            allowNone: false,
          }),
        ],
        { hideLabel: true },
      ),

      epc.subsections.subsection(
        'content.postAction',
        'Success Message',
        [
          epc.fields.textAreaField(
            `${ELEMENT_PATH_WF}nodes.?%hasResultMessages.successMessage`,
            'Message',
            { hideLabel: true },
          ),
          getActionTypeConfig('add'),
          getActionTypeConfig('edit'),
          epc.utils.conditionalField(
            epc.fields.selectFieldFromApi(
              `${ELEMENT_PATH_WF}nodes.?%isNavigateNode.target`,
              'Go to page',
              'LIST_PAGES',
              { allowNone: false, showSelection: true },
            ),
            'content.postAction.actionType',
            (model) => model !== 'Page',
          ),
        ],
        {
          hideWhen: (elementId: string, resolver: IPropEditorResolver) =>
            !resolver.resolveValue('ELEMENT_PATH_VALUE', {
              path: `${ELEMENT_PATH_WF}nodes.?%hasResultMessages`,
              elementId,
            }),
        },
      ),

      epc.subsections.visibilitySubsection(),
    ]),
    epc.section('style', 'Styles', [
      epc.subsections.styleSheetSubsection(),
      epc.subsections.subsection('style.general', 'Padding & Margin', [
        epc.fields.spacingAndAlignField('style.general.offset', {
          hasMargin: true,
          hasPadding: true,
        }),
        ...epc.fields.visualEffectField('style.general.visualEffect'),
      ]),
    ]),
  ]);
}

function getDefaults(): PropsOf<'formGroup'> {
  return {
    content: {
      model: [],
      postAction: {
        actionType: 'None',
      },
    },
    style: {
      general: {
        offset: { margin: '$t.spacingExternal', padding: '$t.paddingContainerLike' },
        visualEffect: {
          type: '$t.effectType',
          data: {
            x: '$t.effectX',
            y: '$t.effectY',
            blur: '$t.effectBlur',
            color1: '$t.effectColor1',
          },
        },
      },
      container: {
        outlineWidth: '$t.containerOutlineWidth',
        outline: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
      },
    },
  };
}

function getInitialValues(): PartialPropsOf<'formGroup'> {
  return {
    content: {
      action: 'add',
    },
  };
}
